import {
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { ProductLine, Plan } from './plan';
import { Invoice } from './invoice';
import { queryCreate, queryDelete, queryGet, queryUpdate } from './fetch';
import { useCallback } from 'react';
import { useSession } from '.';

export enum OrderStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  BILLING_IN_PROGRESS = 'BILLING_IN_PROGRESS',
  BILLED = 'BILLED',
}

export interface Order {
  id: string;
  sessionId: string;
  organizationId: string;
  status: OrderStatus;
  participationId?: string;
  taskId?: string;
  invoices?: Invoice[];
  invoice?: Invoice;
  invoiceId?: string;
  productLines?: ProductLine[];
  participantId?: string;
  plan?: Plan;
  planId?: string;
  sessionLinkId?: string;
  duration?: number;
  incentiveDisplayed?: string;
  completedAt?: Date;
  billedAt?: Date;
  total?: number;
}

export const useSessionOrders = (
  sessionId?: string,
  status: OrderStatus[] = [OrderStatus.COMPLETED, OrderStatus.BILLED],
  options: UseQueryOptions<Order[]> = {},
) => {
  return useQuery<Order[]>(
    ['sessions', sessionId, 'orders', status],
    queryGet(`/v2/sessions/${sessionId}/orders`, { status }),
    {
      enabled: !!sessionId,
      ...options,
    },
  );
};

export const useSessionProductLines = (
  organizationId?: string,
  sessionId?: string,
  status: OrderStatus[] = [OrderStatus.COMPLETED, OrderStatus.BILLED],
) => {
  return useQuery<ProductLine[]>(
    [
      'organization',
      organizationId,
      'sessions',
      sessionId,
      'product-lines',
      status,
    ],
    queryGet(
      `/v2/organization/${organizationId}/sessions/${sessionId}/product-lines`,
      { status },
    ),
    {
      enabled: !!sessionId && !!organizationId,
    },
  );
};

export const useClearOrderCache = (id?: string, sessionId?: string) => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    setTimeout(() => {
      queryClient.invalidateQueries(['orders', id]);
      queryClient.invalidateQueries(['sessions', sessionId, 'orders']);
    }, 500);
  }, [id, queryClient]);
};
export const useCompleteOrder = (id?: string, sessionId?: string) => {
  const clearCache = useClearOrderCache(id, sessionId);
  return useMutation(queryUpdate(`/v2/orders/${id}/complete`), {
    onSuccess: clearCache,
  });
};

export const useCancelOrder = (id?: string, sessionId?: string) => {
  const clearCache = useClearOrderCache(id, sessionId);
  return useMutation(queryUpdate(`/v2/orders/${id}/cancel`), {
    onSuccess: clearCache,
  });
};

interface IdDto {
  id: string;
}

export const useAddProductToOrder = (id?: string, sessionId?: string) => {
  const clearCache = useClearOrderCache(id, sessionId);

  return useMutation<Order, undefined, IdDto>(
    queryCreate(`/v2/orders/${id}/products`),
    {
      onSuccess: clearCache,
    },
  );
};

export const useRemoveProductFromOrder = (id?: string, sessionId?: string) => {
  const clearCache = useClearOrderCache(id, sessionId);

  return useMutation<Order, undefined, IdDto>(
    queryDelete(`/v2/orders/${id}/products`),
    {
      onSuccess: clearCache,
    },
  );
};

export const useAddProductToSessionOrders = (
  sessionId?: string,
  options: UseMutationOptions<Order, Error, IdDto> = {},
) => {
  const { data: session } = useSession(sessionId);
  const queryClient = useQueryClient();

  return useMutation<Order, Error, IdDto>(
    queryCreate(`/v2/plans/${session?.planId}/orders/products`),
    {
      ...options,
      onSuccess: (data, variables, ctx) => {
        queryClient.invalidateQueries(['sessions', sessionId, 'orders']);
        queryClient.invalidateQueries([
          'organization',
          session?.organizationId,
          'sessions',
          sessionId,
          'product-lines',
        ]);
        if (options.onSuccess) {
          options.onSuccess(data, variables, ctx);
        }
      },
    },
  );
};

export const useRemoveProductToSessionOrders = (
  sessionId?: string,
  options: UseMutationOptions<Order, Error, IdDto> = {},
) => {
  const { data: session } = useSession(sessionId);
  const queryClient = useQueryClient();

  return useMutation<Order, Error, IdDto>(
    queryDelete(`/v2/plans/${session?.planId}/orders/products`),
    {
      ...options,
      onSuccess: (data, variables, ctx) => {
        queryClient.invalidateQueries(['sessions', sessionId, 'orders']);
        queryClient.invalidateQueries([
          'organization',
          session?.organizationId,
          'sessions',
          sessionId,
          'product-lines',
        ]);
        if (options.onSuccess) {
          options.onSuccess(data, variables, ctx);
        }
      },
    },
  );
};
